import { useState } from "react";
import { Sparkles, Loader2, Copy, Check, ListTodo, FileText, X, Lightbulb, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAIAssist, type SummarizeResult } from "@/hooks/useAIAssist";
import { toast } from "sonner";

interface AIAssistPanelProps {
  meetingId: string;
  isHost: boolean;
}

type Focus = "summary" | "key_points" | "action_items";

const FOCUS_STORAGE_KEY = "meethub:ai-panel:lastFocus";

const readStoredFocus = (): Focus => {
  if (typeof window === "undefined") return "summary";
  const v = window.localStorage.getItem(FOCUS_STORAGE_KEY);
  return v === "key_points" || v === "action_items" || v === "summary" ? v : "summary";
};

export function AIAssistPanel({ meetingId, isHost }: AIAssistPanelProps) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<SummarizeResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [focus, setFocusState] = useState<Focus>(() => readStoredFocus());
  const { call, loading, lastError, clearError } = useAIAssist();

  const setFocus = (f: Focus) => {
    setFocusState(f);
    try {
      window.localStorage.setItem(FOCUS_STORAGE_KEY, f);
    } catch {
      // Ignore storage failures; focus still updates in memory.
    }
  };

  if (!isHost) return null;

  const isLoading = loading === "summarize";

  const run = async (nextFocus: Focus) => {
    setFocus(nextFocus);
    clearError();
    const data = await call<SummarizeResult>({
      mode: "summarize",
      meetingId,
      source: "transcript",
    });
    if (data) {
      setResult(data);
      const label =
        nextFocus === "summary"
          ? "Summary ready"
          : nextFocus === "key_points"
            ? "Key points captured"
            : "Action items generated";
      toast.success(label);
    }
  };

  const copyAll = () => {
    if (!result) return;
    const text = [
      "Summary:",
      result.summary,
      "",
      "Key points:",
      ...result.key_points.map((p) => `• ${p}`),
      "",
      "Action items:",
      ...result.action_items.map((a) => `• ${a.owner}: ${a.task}`),
    ].join("\n");
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed bottom-24 right-4 z-40 sm:bottom-28 sm:right-6">
      {!open ? (
        <Button
          onClick={() => setOpen(true)}
          size="lg"
          className="h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90 animate-fade-in"
          aria-label="Open AI assistant"
        >
          <Sparkles className="h-6 w-6" />
        </Button>
      ) : (
        <Card className="w-[340px] sm:w-[380px] shadow-xl border-border/60 backdrop-blur-md bg-background/95 animate-scale-in">
          <div className="flex items-center justify-between p-3 border-b">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold">AI Assistant</p>
                <p className="text-[11px] text-muted-foreground">Tap to capture insights</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="p-3 grid grid-cols-3 gap-2">
            {([
              { id: "summary" as const, icon: FileText, label: "Summarize" },
              { id: "key_points" as const, icon: Lightbulb, label: "Key Points" },
              { id: "action_items" as const, icon: ListTodo, label: "Actions" },
            ]).map((b) => {
              const Icon = b.icon;
              const active = isLoading && focus === b.id;
              return (
                <Button
                  key={b.id}
                  variant={focus === b.id && result ? "default" : "outline"}
                  size="sm"
                  className="flex-col h-auto py-2 gap-1"
                  disabled={isLoading}
                  onClick={() => run(b.id)}
                >
                  {active ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                  <span className="text-[11px]">{b.label}</span>
                </Button>
              );
            })}
          </div>

          {/* Error state with retry */}
          {lastError && !isLoading && (
            <div className="mx-3 mb-3 rounded-md border border-destructive/30 bg-destructive/5 p-2.5 space-y-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-[12px] leading-relaxed">
                  {lastError.kind === "rate_limit"
                    ? "AI is busy. Try again in a moment."
                    : lastError.kind === "credits"
                      ? "AI credits exhausted. Add credits in workspace settings."
                      : lastError.kind === "network"
                        ? "Network issue — check your connection."
                        : lastError.message || "Could not generate insights."}
                </p>
              </div>
              <div className="flex justify-end gap-1">
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px]" onClick={clearError}>
                  Dismiss
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[12px]"
                  onClick={() => run(focus)}
                  disabled={lastError.kind === "credits"}
                >
                  <RefreshCw className="h-3 w-3 mr-1" /> Retry
                </Button>
              </div>
            </div>
          )}

          {result && !lastError && (
            <ScrollArea className="max-h-[320px] px-3 pb-3">
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    Summary
                  </p>
                  <p className="leading-relaxed">{result.summary}</p>
                </div>
                {result.key_points.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      Key points
                    </p>
                    <ul className="space-y-1 list-disc list-inside text-[13px]">
                      {result.key_points.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.action_items.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      Action items
                    </p>
                    <ul className="space-y-1 text-[13px]">
                      {result.action_items.map((a, i) => (
                        <li key={i}>
                          <span className="font-medium">{a.owner}:</span> {a.task}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <Button size="sm" variant="ghost" className="w-full gap-2" onClick={copyAll}>
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy all"}
                </Button>
              </div>
            </ScrollArea>
          )}

          {!result && !isLoading && !lastError && (
            <p className="px-3 pb-3 text-[12px] text-muted-foreground">
              AI runs only when you ask. Pick an action above.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

export default AIAssistPanel;
